import React from 'react';
import { useNavigate } from 'react-router-dom';

const FoodSafetyLandingPage: React.FC = () => {
    const navigate = useNavigate();

    return (
        <div className="bg-gray-50 font-sans text-left">
            {/* Navigation */}
            <nav className="bg-white shadow-lg sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2">
                                <div className="w-8 h-8 bg-orange-600 rounded-lg flex items-center justify-center">
                                    <i className="fas fa-utensils text-white text-sm"></i>
                                </div>
                                <span className="text-xl font-bold text-gray-900">Food Safety Pro UK</span>
                            </div>
                        </div>
                        <div className="hidden md:flex items-center space-x-8">
                            <a href="#features" className="text-gray-700 hover:text-orange-600 transition-colors">Features</a>
                            <a href="#pricing" className="text-gray-700 hover:text-orange-600 transition-colors">Pricing</a>
                            <a href="#demo" className="text-gray-700 hover:text-orange-600 transition-colors">Demo</a>
                            <a href="#" className="text-gray-700 hover:text-orange-600 transition-colors font-medium">Login</a>
                            <button
                                onClick={() => navigate('/signup')}
                                className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
                            >
                                Start Free Trial
                            </button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="bg-gradient-to-br from-orange-600 via-orange-700 to-red-800 text-white py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h1 className="text-5xl font-bold mb-6">Food Safety Pro UK</h1>
                            <p className="text-xl text-orange-100 mb-8">
                                Professional food safety management with HACCP templates, allergen tracking, hygiene rating monitoring, and staff training records.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4">
                                <button
                                    onClick={() => navigate('/signup')}
                                    className="bg-yellow-400 text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-yellow-300 transition-colors"
                                >
                                    Start Free Trial
                                </button>
                                <button
                                    onClick={() => document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth' })}
                                    className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors"
                                >
                                    View Demo
                                </button>
                            </div >
                            <div className="flex items-center space-x-8 text-sm mt-8">
                                <div className="flex items-center space-x-2">
                                    <i className="fas fa-check-circle text-green-400"></i>
                                    <span>HACCP Management</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <i className="fas fa-check-circle text-green-400"></i>
                                    <span>FSA Integration</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <i className="fas fa-check-circle text-green-400"></i>
                                    <span>Allergen Tracking</span>
                                </div>
                            </div>
                        </div >
                        <div className="bg-white rounded-2xl shadow-2xl p-8 text-gray-900">
                            <h3 className="text-2xl font-bold mb-6">Food Safety Dashboard</h3>
                            <div className="grid grid-cols-2 gap-4 mb-6">
                                <div className="text-center">
                                    <div className="text-3xl font-bold text-orange-600">91%</div>
                                    <p className="text-gray-600">Compliance Score</p>
                                </div>
                                <div className="text-center">
                                    <div className="text-3xl font-bold text-blue-600">3</div>
                                    <p className="text-gray-600">Establishments</p>
                                </div>
                            </div>
                            <div className="space-y-3">
                                <div className="flex justify-between items-center">
                                    <span>HACCP Plans</span>
                                    <span className="text-green-600 font-semibold">All Active</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span>Allergen Management</span>
                                    <span className="text-green-600 font-semibold">Compliant</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span>Hygiene Ratings</span>
                                    <span className="text-blue-600 font-semibold">4-5 Stars</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span>Staff Training</span>
                                    <span className="text-yellow-600 font-semibold">2 Due Soon</span>
                                </div>
                            </div>
                        </div>
                    </div >
                </div >
            </section >

            {/* Features Section */}
            < section id="features" className="py-20 bg-white" >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold text-gray-900 mb-4">Complete Food Safety Solution</h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Everything you need for food safety compliance, from HACCP management to allergen tracking and staff training
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        <div className="text-center">
                            <div className="w-20 h-20 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                                <i className="fas fa-clipboard-check text-3xl text-orange-600"></i>
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-4">HACCP Management</h3>
                            <p className="text-gray-600">Comprehensive HACCP plan templates with critical control point monitoring and automated alerts.</p>
                        </div>

                        <div className="text-center">
                            <div className="w-20 h-20 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                                <i className="fas fa-allergies text-3xl text-red-600"></i>
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-4">Allergen Tracking</h3>
                            <p className="text-gray-600">Complete allergen ingredient database with menu labeling compliance and cross-contamination prevention.</p>
                        </div>

                        <div className="text-center">
                            <div className="w-20 h-20 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                                <i className="fas fa-star text-3xl text-blue-600"></i>
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-4">Hygiene Ratings</h3>
                            <p className="text-gray-600">Monitor and improve food hygiene ratings with comprehensive audit preparation and compliance tracking.</p>
                        </div>

                        <div className="text-center">
                            <div className="w-20 h-20 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                                <i className="fas fa-graduation-cap text-3xl text-green-600"></i>
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-4">Staff Training</h3>
                            <p className="text-gray-600">Comprehensive training management with certification tracking and competency assessments.</p>
                        </div>
                    </div>
                </div>
            </section >

            {/* Interactive Demo Section */}
            < section id="demo" className="py-20 bg-gray-50" >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold text-gray-900 mb-4">Food Safety Management Dashboard</h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Monitor food safety across all your establishments with real-time temperature monitoring and compliance tracking
                        </p>
                    </div>

                    <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                        {/* Dashboard Header */}
                        <div className="bg-gradient-to-r from-orange-600 to-orange-700 px-8 py-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-2xl font-bold text-white">Food Safety Dashboard</h3>
                                    <p className="text-orange-100">3 establishments under management</p>
                                </div>
                                <div className="flex items-center space-x-4">
                                    <button className="bg-white bg-opacity-20 text-white px-4 py-2 rounded-lg hover:bg-opacity-30 transition-colors">
                                        <i className="fas fa-plus mr-2"></i>Add Establishment
                                    </button>
                                    <button className="bg-white bg-opacity-20 text-white px-4 py-2 rounded-lg hover:bg-opacity-30 transition-colors">
                                        <i className="fas fa-download mr-2"></i>Export Report
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Dashboard Content */}
                        <div className="p-8">
                            {/* Establishment Overview */}
                            <div className="grid md:grid-cols-3 gap-6 mb-8">
                                {/* Establishment 1 */}
                                <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow">
                                    <div className="flex justify-between items-start mb-4">
                                        <div>
                                            <h4 className="text-lg font-semibold text-gray-900">The Garden Bistro</h4>
                                            <p className="text-sm text-gray-600">London, NW1 6XE</p>
                                        </div>
                                        <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                                            <i className="fas fa-utensils text-green-600 text-xl"></i>
                                        </div>
                                    </div>
                                    <div className="space-y-3 mb-4">
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-600">Hygiene Rating</span>
                                            <span className="text-sm font-semibold text-green-600">5 Stars</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-600">HACCP Status</span>
                                            <span className="text-sm font-semibold text-green-600">Active</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-600">Staff Trained</span>
                                            <span className="text-sm font-semibold text-blue-600">12/12</span>
                                        </div>
                                    </div>
                                    <div className="flex space-x-2">
                                        <button className="flex-1 bg-green-600 text-white py-2 px-3 rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors">
                                            View Details
                                        </button>
                                        <button className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                                            <i className="fas fa-edit"></i>
                                        </button>
                                    </div>
                                </div>

                                {/* Establishment 2 */}
                                <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow">
                                    <div className="flex justify-between items-start mb-4">
                                        <div>
                                            <h4 className="text-lg font-semibold text-gray-900">City Cafe</h4>
                                            <p className="text-sm text-gray-600">London, EC1A 1AA</p>
                                        </div>
                                        <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                                            <i className="fas fa-coffee text-yellow-600 text-xl"></i>
                                        </div>
                                    </div>
                                    <div className="space-y-3 mb-4">
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-600">Hygiene Rating</span>
                                            <span className="text-sm font-semibold text-yellow-600">4 Stars</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-600">HACCP Status</span>
                                            <span className="text-sm font-semibold text-green-600">Active</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-600">Staff Trained</span>
                                            <span className="text-sm font-semibold text-yellow-600">8/10</span>
                                        </div>
                                    </div>
                                    <div className="flex space-x-2">
                                        <button className="flex-1 bg-yellow-600 text-white py-2 px-3 rounded-lg text-sm font-semibold hover:bg-yellow-700 transition-colors">
                                            Complete Training
                                        </button>
                                        <button className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                                            <i className="fas fa-edit"></i>
                                        </button>
                                    </div>
                                </div>

                                {/* Establishment 3 */}
                                <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow">
                                    <div className="flex justify-between items-start mb-4">
                                        <div>
                                            <h4 className="text-lg font-semibold text-gray-900">Riverside Restaurant</h4>
                                            <p className="text-sm text-gray-600">London, SE1 9PH</p>
                                        </div>
                                        <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                                            <i className="fas fa-fish text-green-600 text-xl"></i>
                                        </div>
                                    </div>
                                    <div className="space-y-3 mb-4">
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-600">Hygiene Rating</span>
                                            <span className="text-sm font-semibold text-green-600">5 Stars</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-600">HACCP Status</span>
                                            <span className="text-sm font-semibold text-green-600">Active</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-600">Staff Trained</span>
                                            <span className="text-sm font-semibold text-green-600">15/15</span>
                                        </div>
                                    </div>
                                    <div className="flex space-x-2">
                                        <button className="flex-1 bg-green-600 text-white py-2 px-3 rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors">
                                            View Details
                                        </button>
                                        <button className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                                            <i className="fas fa-edit"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* HACCP & Allergen Management */}
                            <div className="grid md:grid-cols-2 gap-8 mb-8">
                                <div className="bg-gray-50 rounded-xl p-6">
                                    <h4 className="text-lg font-semibold text-gray-900 mb-4">HACCP Critical Control Points</h4>
                                    <div className="space-y-4">
                                        <div className="bg-white rounded-lg p-4 border border-gray-200">
                                            <div className="flex justify-between items-center">
                                                <div className="flex items-center space-x-3">
                                                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                                        <i className="fas fa-thermometer-half text-blue-600"></i>
                                                    </div>
                                                    <div>
                                                        <h5 className="font-semibold text-gray-900">Temperature Control</h5>
                                                        <p className="text-sm text-gray-600">Cold storage monitoring</p>
                                                    </div>
                                                </div>
                                                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                                                    Normal
                                                </span>
                                            </div>
                                        </div>
                                        <div className="bg-white rounded-lg p-4 border border-gray-200">
                                            <div className="flex justify-between items-center">
                                                <div className="flex items-center space-x-3">
                                                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                                                        <i className="fas fa-fire text-red-600"></i>
                                                    </div>
                                                    <div>
                                                        <h5 className="font-semibold text-gray-900">Cooking Temperature</h5>
                                                        <p className="text-sm text-gray-600">High temperature cooking</p>
                                                    </div>
                                                </div>
                                                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                                                    Compliant
                                                </span>
                                            </div>
                                        </div>
                                        <div className="bg-white rounded-lg p-4 border border-gray-200">
                                            <div className="flex justify-between items-center">
                                                <div className="flex items-center space-x-3">
                                                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                                        <i className="fas fa-hands-wash text-purple-600"></i>
                                                    </div>
                                                    <div>
                                                        <h5 className="font-semibold text-gray-900">Hygiene Practices</h5>
                                                        <p className="text-sm text-gray-600">Personal hygiene monitoring</p>
                                                    </div>
                                                </div>
                                                <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-semibold">
                                                    Review Due
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-gray-50 rounded-xl p-6">
                                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Allergen Management</h4>
                                    <div className="space-y-4">
                                        <div className="bg-white rounded-lg p-4 border border-gray-200">
                                            <div className="flex justify-between items-center">
                                                <div className="flex items-center space-x-3">
                                                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                                                        <i className="fas fa-exclamation-triangle text-red-600"></i>
                                                    </div>
                                                    <div>
                                                        <h5 className="font-semibold text-gray-900">Menu Labeling</h5>
                                                        <p className="text-sm text-gray-600">Allergen information display</p>
                                                    </div>
                                                </div>
                                                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                                                    Compliant
                                                </span>
                                            </div>
                                        </div>
                                        <div className="bg-white rounded-lg p-4 border border-gray-200">
                                            <div className="flex justify-between items-center">
                                                <div className="flex items-center space-x-3">
                                                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                                                        <i className="fas fa-utensils text-orange-600"></i>
                                                    </div>
                                                    <div>
                                                        <h5 className="font-semibold text-gray-900">Cross-Contamination</h5>
                                                        <p className="text-sm text-gray-600">Prevention protocols</p>
                                                    </div>
                                                </div>
                                                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                                                    Controlled
                                                </span>
                                            </div>
                                        </div>
                                        <div className="bg-white rounded-lg p-4 border border-gray-200">
                                            <div className="flex justify-between items-center">
                                                <div className="flex items-center space-x-3">
                                                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                                        <i className="fas fa-database text-blue-600"></i>
                                                    </div>
                                                    <div>
                                                        <h5 className="font-semibold text-gray-900">Ingredient Database</h5>
                                                        <p className="text-sm text-gray-600">Allergen ingredient tracking</p>
                                                    </div>
                                                </div>
                                                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                                                    Updated
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* Staff Training & Temperature Monitoring would go here... */}
                        </div>
                    </div>
                </div>
            </section >

            {/* FSA Integration Section */}
            < section className="py-20 bg-white" >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h2 className="text-4xl font-bold text-gray-900 mb-6">Food Standards Agency Integration</h2>
                            <p className="text-xl text-gray-600 mb-8">
                                Direct integration with the Food Standards Agency for hygiene rating monitoring, inspection scheduling, and regulatory updates.
                            </p>
                            <div className="space-y-6">
                                <div className="flex items-start space-x-4">
                                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                                        <i className="fas fa-star text-blue-600"></i>
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900 mb-2">Hygiene Rating Monitoring</h4>
                                        <p className="text-gray-600">Real-time monitoring of hygiene ratings across all establishments</p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-4">
                                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                                        <i className="fas fa-calendar-check text-green-600"></i>
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900 mb-2">Inspection Scheduling</h4>
                                        <p className="text-gray-600">Automated preparation for environmental health visits</p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-4">
                                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                                        <i className="fas fa-bell text-purple-600"></i>
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900 mb-2">Regulatory Updates</h4>
                                        <p className="text-gray-600">Instant notifications of FSA guidance changes and new regulations</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="bg-gray-50 rounded-2xl p-8">
                            <h3 className="text-2xl font-bold text-gray-900 mb-6">FSA Dashboard</h3>
                            <div className="space-y-4">
                                <div className="bg-white rounded-lg p-4 border border-gray-200">
                                    <div className="flex justify-between items-center">
                                        <span className="font-medium text-gray-900">Overall Rating</span>
                                        <div className="flex space-x-1">
                                            <i className="fas fa-star text-yellow-400"></i>
                                            <i className="fas fa-star text-yellow-400"></i>
                                            <i className="fas fa-star text-yellow-400"></i>
                                            <i className="fas fa-star text-yellow-400"></i>
                                            <i className="fas fa-star text-yellow-400"></i>
                                        </div>
                                    </div>
                                    <p className="text-sm text-gray-600 mt-1">Average across all establishments</p>
                                </div>
                                <div className="bg-white rounded-lg p-4 border border-gray-200">
                                    <div className="flex justify-between items-center">
                                        <span className="font-medium text-gray-900">Last Inspection</span>
                                        <span className="text-gray-600">2 weeks ago</span>
                                    </div>
                                </div>
                                <div className="bg-white rounded-lg p-4 border border-gray-200">
                                    <div className="flex justify-between items-center">
                                        <span className="font-medium text-gray-900">Next Inspection Due</span>
                                        <span className="text-gray-600">March 2025</span>
                                    </div>
                                </div>
                                <div className="bg-white rounded-lg p-4 border border-gray-200">
                                    <div className="flex justify-between items-center">
                                        <span className="font-medium text-gray-900">Compliance Alerts</span>
                                        <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                                            None
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section >

            {/* Pricing Section */}
            < section id="pricing" className="py-20 bg-gray-50" >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold text-gray-900 mb-4">Simple, Transparent Pricing</h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Choose the plan that fits your food business needs. All plans include our core food safety features.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {/* Starter Plan */}
                        <div className="bg-white rounded-2xl shadow-lg p-8">
                            <div className="text-center mb-8">
                                <h3 className="text-2xl font-bold text-gray-900 mb-2">Starter</h3>
                                <p className="text-gray-600 mb-4">Single establishment</p>
                                <div className="text-4xl font-bold text-gray-900 mb-2">£59<span className="text-lg font-normal text-gray-600">/month</span></div>
                                <p className="text-sm text-gray-500">Billed annually</p>
                            </div>
                            <ul className="space-y-4 mb-8">
                                <li className="flex items-center space-x-3">
                                    <i className="fas fa-check text-green-500"></i>
                                    <span>HACCP management</span>
                                </li>
                                <li className="flex items-center space-x-3">
                                    <i className="fas fa-check text-green-500"></i>
                                    <span>Allergen tracking</span>
                                </li>
                                <li className="flex items-center space-x-3">
                                    <i className="fas fa-check text-green-500"></i>
                                    <span>Basic reporting</span>
                                </li>
                                <li className="flex items-center space-x-3">
                                    <i className="fas fa-check text-green-500"></i>
                                    <span>Email support</span>
                                </li>
                            </ul>
                            <button className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors">
                                Start Free Trial
                            </button>
                        </div>

                        {/* Professional Plan */}
                        <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-orange-600 relative">
                            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                                <span className="bg-orange-600 text-white px-4 py-1 rounded-full text-sm font-semibold">Most Popular</span>
                            </div>
                            <div className="text-center mb-8">
                                <h3 className="text-2xl font-bold text-gray-900 mb-2">Professional</h3>
                                <p className="text-gray-600 mb-4">Up to 3 establishments</p>
                                <div className="text-4xl font-bold text-gray-900 mb-2">£149<span className="text-lg font-normal text-gray-600">/month</span></div>
                                <p className="text-sm text-gray-500">Billed annually</p>
                            </div>
                            <ul className="space-y-4 mb-8">
                                <li className="flex items-center space-x-3">
                                    <i className="fas fa-check text-green-500"></i>
                                    <span>Up to 3 establishments</span>
                                </li>
                                <li className="flex items-center space-x-3">
                                    <i className="fas fa-check text-green-500"></i>
                                    <span>FSA integration</span>
                                </li>
                                <li className="flex items-center space-x-3">
                                    <i className="fas fa-check text-green-500"></i>
                                    <span>IoT temperature monitoring</span>
                                </li>
                                <li className="flex items-center space-x-3">
                                    <i className="fas fa-check text-green-500"></i>
                                    <span>Advanced reporting</span>
                                </li>
                                <li className="flex items-center space-x-3">
                                    <i className="fas fa-check text-green-500"></i>
                                    <span>Priority support</span>
                                </li>
                            </ul>
                            <button className="w-full bg-orange-600 text-white py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors">
                                Start Free Trial
                            </button>
                        </div>

                        {/* Enterprise Plan */}
                        <div className="bg-white rounded-2xl shadow-lg p-8">
                            <div className="text-center mb-8">
                                <h3 className="text-2xl font-bold text-gray-900 mb-2">Enterprise</h3>
                                <p className="text-gray-600 mb-4">Unlimited establishments</p>
                                <div className="text-4xl font-bold text-gray-900 mb-2">£299<span className="text-lg font-normal text-gray-600">/month</span></div>
                                <p className="text-sm text-gray-500">Billed annually</p>
                            </div>
                            <ul className="space-y-4 mb-8">
                                <li className="flex items-center space-x-3">
                                    <i className="fas fa-check text-green-500"></i>
                                    <span>Unlimited establishments</span>
                                </li>
                                <li className="flex items-center space-x-3">
                                    <i className="fas fa-check text-green-500"></i>
                                    <span>All features included</span>
                                </li>
                                <li className="flex items-center space-x-3">
                                    <i className="fas fa-check text-green-500"></i>
                                    <span>Custom integrations</span>
                                </li>
                                <li className="flex items-center space-x-3">
                                    <i className="fas fa-check text-green-500"></i>
                                    <span>SLA guarantee</span>
                                </li>
                                <li className="flex items-center space-x-3">
                                    <i className="fas fa-check text-green-500"></i>
                                    <span>Dedicated support</span>
                                </li>
                            </ul>
                            <button className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors">
                                Contact Sales
                            </button>
                        </div>
                    </div>
                </div>
            </section >

            {/* CTA Section */}
            < section className="py-20 bg-gradient-to-r from-orange-600 to-orange-800 text-white" >
                <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
                    <h2 className="text-4xl font-bold mb-4">Ready for Food Safety Compliance?</h2>
                    <p className="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">
                        Join thousands of UK food businesses who trust our platform for complete food safety and allergen compliance.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <button
                            onClick={() => navigate('/signup')}
                            className="bg-yellow-400 text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-yellow-300 transition-colors"
                        >
                            Start Your Free Trial
                        </button>
                        <button className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors">
                            Schedule a Demo
                        </button>
                    </div>
                    <p className="text-sm text-orange-100 mt-4">
                        No credit card required • 14-day free trial • Cancel anytime
                    </p>
                </div>
            </section >

            {/* Footer */}
            < footer className="bg-gray-900 text-white py-12 mt-16" >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-4 gap-8 mb-8">
                        <div>
                            <div className="flex items-center space-x-2 mb-4">
                                <div className="w-8 h-8 bg-orange-600 rounded-lg flex items-center justify-center">
                                    <i className="fas fa-utensils text-white text-sm"></i>
                                </div>
                                <span className="text-xl font-bold">Food Safety Pro</span>
                            </div>
                            <p className="text-gray-400 text-sm">Professional food safety management for UK businesses.</p>
                        </div>

                        <div>
                            <h4 className="font-semibold mb-4 text-sm">Product</h4>
                            <ul className="space-y-2 text-sm text-gray-400">
                                <li><a href="#" className="hover:text-white transition-colors">Features</a></li>
                                <li><a href="#" className="hover:text-white transition-colors">Pricing</a></li>
                                <li><a href="#" className="hover:text-white transition-colors">Demo</a></li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="font-semibold mb-4 text-sm">Support</h4>
                            <ul className="space-y-2 text-sm text-gray-400">
                                <li><a href="#" className="hover:text-white transition-colors">Help Center</a></li>
                                <li><a href="#" className="hover:text-white transition-colors">Documentation</a></li>
                                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="font-semibold mb-4 text-sm">Legal</h4>
                            <ul className="space-y-2 text-sm text-gray-400">
                                <li><a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Privacy Policy</a></li>
                                <li><a href="/dpa-template.html" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">DPA Template</a></li>
                                <li><a href="/data-exit-policy.html" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Data Exit Policy</a></li>
                                <li><a href="/terms-of-service.html" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Terms of Service</a></li>
                                <li><a href="/refund-policy.html" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Refund & Cancellation</a></li>
                            </ul>
                        </div>
                    </div>

                    <div className="border-t border-gray-800 pt-8 text-center">
                        <p className="text-sm text-gray-400">&copy; {new Date().getFullYear()} Food Safety Pro UK. All rights reserved.</p>
                    </div>
                </div>
            </footer >
        </div >
    );
};

export default FoodSafetyLandingPage;
